// frontend/src/features/citysense/housing-stock-modeller/utils/geojsonLoader.ts

import { HousingPolygon } from "../types";
import { getArchetypeColor, resetColorIndex } from "../constants/housingConfig";

interface GeoJSONFeature {
  type: "Feature";
  properties: {
    Household_cluster_name: string;
    Index22_ED_rel_wt_lab: string;
    Houses_2022: number;
    Flats_2022: number;
    Dwellings_2022: number;
  };
  geometry: {
    type: "Polygon";
    coordinates: number[][][];
  };
}

interface GeoJSONData {
  type: "FeatureCollection";
  features: GeoJSONFeature[];
}

// Normalize EPSG:2157 coordinates to 0-2000 range
function normalizeCoordinates(features: GeoJSONFeature[]): {
  normalized: Array<{
    type: "Feature";
    properties: GeoJSONFeature["properties"];
    geometry: {
      type: "Polygon";
      coordinates: [number, number][][];
    };
  }>;
} {
  let minX = Infinity,
    maxX = -Infinity;
  let minY = Infinity,
    maxY = -Infinity;

  // Find bounds
  features.forEach((feature) => {
    feature.geometry.coordinates[0].forEach((coord) => {
      const x = coord[0];
      const y = coord[1];
      minX = Math.min(minX, x);
      maxX = Math.max(maxX, x);
      minY = Math.min(minY, y);
      maxY = Math.max(maxY, y);
    });
  });

  const width = maxX - minX;
  const height = maxY - minY;
  const scale = 2000 / Math.max(width, height);

  // Normalize to 0-2000
  const normalized = features.map((feature) => ({
    type: "Feature" as const,
    properties: feature.properties,
    geometry: {
      type: "Polygon" as const,
      coordinates: [
        feature.geometry.coordinates[0].map((coord): [number, number] => [
          (coord[0] - minX) * scale,
          2000 - (coord[1] - minY) * scale, // Flip Y for SVG
        ]),
      ],
    },
  }));

  return { normalized };
}

export function loadHousingPolygons(geojsonData: GeoJSONData): {
  polygons: HousingPolygon[];
  archetypeColors: Map<string, string>;
} {
  const { normalized } = normalizeCoordinates(geojsonData.features);

  // Build unique archetype list and assign colors
  const uniqueArchetypes = new Set<string>();
  normalized.forEach((f) =>
    uniqueArchetypes.add(f.properties.Household_cluster_name),
  );

resetColorIndex();
const archetypeColors = new Map<string, string>();
Array.from(uniqueArchetypes).sort().forEach((archetype) => {
  archetypeColors.set(archetype, getArchetypeColor(archetype)); // Pass archetype name
});

  const polygons = normalized.map((feature, idx) => ({
    id: `poly${idx}`,
    archetype: feature.properties.Household_cluster_name,
    deprivationIndex:
      feature.properties.Index22_ED_rel_wt_lab || "Marginally Above Average",
    houses: feature.properties.Houses_2022 || 0,
    flats: feature.properties.Flats_2022 || 0,
    dwellings: feature.properties.Dwellings_2022 || 0,
    pts: feature.geometry.coordinates[0],
  }));

  return { polygons, archetypeColors };
}
